import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'mantine-react-table'
import { Badge } from '@mantine/core'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import {
  convertPhoneNumberToString,
  findLabelByValue,
  formatDateForTable,
} from '@/shared/lib/utils'
import { LandingFormsListItem } from '@/shared/api/topli/TopliApi'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { eventTypeColors, getDetails } from './consts'
import { useLandingNotificationLog, useLandingViews } from '@/entities/LandingNotificationLog'

export function useLandingEventsTable(userId: number) {
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { landingFormTypesOptions } = useTranslatedEnums()

  const { t } = useTranslation(['userManagement', 'common'])
  const { language } = useLanguageStore()

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const selectedActionIds = (columnFilters.find(f => f.id === 'form_type')?.value ?? []) as number[]

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [columnFilters])

  const {
    data: notifications,
    isLoading: isLoadingNotifications,
    isError: isErrorNotifications,
    isFetching: isFetchingNotifications,
  } = useLandingNotificationLog({
    sort: memoizedSorting,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    user_id: userId,
    events: selectedActionIds,
  })

  const { data: numberOfLandingViews, isLoading: isLoadingLandingViews } = useLandingViews(userId)

  const columns = useMemo<MRT_ColumnDef<LandingFormsListItem>[]>(
    () => [
      {
        header: t('userManagement:client_name'),
        accessorKey: 'client_name',
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>{row.original.client_name ?? '-'}</div>
        ),
      },
      {
        header: t('common:library_items.event_type.sing'),
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: landingFormTypesOptions.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        },
        accessorKey: 'form_type',
        Cell: ({ row }) => (
          <Badge size='sm' color={eventTypeColors[row.original.form_type]}>
            {findLabelByValue(landingFormTypesOptions, row.original.form_type)}
          </Badge>
        ),
      },
      {
        header: t('userManagement:landing_table.phone'),
        accessorKey: 'phone',
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div className='text-sm'>{convertPhoneNumberToString(row.original.phone ?? null)}</div>
        ),
      },
      {
        header: t('userManagement:landing_table.details'),
        accessorKey: 'details',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => <div className='text-sm'>{getDetails(row.original)}</div>,
      },
      {
        header: t('userManagement:created_at'),
        enableColumnFilter: false,
        accessorKey: 'created_at',
        Cell: ({ row }) => formatDateForTable(row.original.created_at, language),
      },
    ],
    [language],
  )

  return {
    fetchedNotifications: notifications?.data ?? [],
    totalRowCount: notifications?.total ?? 0,
    numberOfLandingViews: numberOfLandingViews?.views ?? 0,
    columns,
    pagination,
    isErrorNotifications,
    isFetchingNotifications,
    isLoadingNotifications,
    sorting,
    setPagination,
    setSorting,
    columnFilters,
    setColumnFilters,
  }
}
